import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import useCreateReducer from '@/hooks/useCreateReducer';
import PromptbarContext from './PromptBar.context';
import { initialState } from './Promptbar.state';
import Icon from '@/components/redesign/icons/Icons';
import { useChat } from '@/contexts/ChatContext';
import { savePrompts } from '@/libs/app/prompts';
import { t } from 'i18next';
import { v4 as uuidv4 } from 'uuid';

const PromptbarSettings = dynamic(() =>
  import('./components/PromptbarSettings').then(
    (module) => module.PromptbarSettings,
  ),
);

const PromptFolders = dynamic(() =>
  import('./components/PromptFolders').then((module) => module.PromptFolders),
);

const PromptModal = dynamic(() =>
  import('./components/PromptModal').then((module) => module.PromptModal),
);

const Promptbar = ({ handleSend }) => {
  const promptBarContextValue = useCreateReducer({
    initialState,
  });

  const {
    state: { prompts, showPromptbar },
    dispatch: homeDispatch,
    handleCreateFolder,
  } = useChat();

  const {
    state: { searchTerm, filteredPrompts },
    dispatch: promptDispatch,
  } = promptBarContextValue;

  const handleTogglePromptbar = () => {
    homeDispatch({ field: 'showPromptbar', value: !showPromptbar });
    localStorage.setItem('showPromptbar', JSON.stringify(!showPromptbar));
  };

  const handleCreatePrompt = () => {
    const newPrompt = {
      id: uuidv4(),
      name: `Prompt ${prompts.length + 1}`,
      description: '',
      content: '',
      folderId: null,
    };

    const updatedPrompts = [...prompts, newPrompt];

    homeDispatch({ field: 'prompts', value: updatedPrompts });

    savePrompts(updatedPrompts);
  };

  const handleDeletePrompt = (prompt) => {
    const updatedPrompts = prompts.filter((p) => p.id !== prompt.id);

    homeDispatch({ field: 'prompts', value: updatedPrompts });
    savePrompts(updatedPrompts);
  };

  const handleUpdatePrompt = (prompt) => {
    const updatedPrompts = prompts.map((p) => {
      if (p.id === prompt.id) {
        return prompt;
      }

      return p;
    });
    homeDispatch({ field: 'prompts', value: updatedPrompts });

    savePrompts(updatedPrompts);
  };

  const handleDrop = (e) => {
    if (e.dataTransfer) {
      const prompt = JSON.parse(e.dataTransfer.getData('prompt'));

      const updatedPrompt = {
        ...prompt,
        folderId: e.target.dataset.folderId,
      };

      handleUpdatePrompt(updatedPrompt);

      e.target.style.removeProperty('background');
    }
  };

  useEffect(() => {
    if (searchTerm) {
      promptDispatch({
        field: 'filteredPrompts',
        value: prompts.filter((prompt) => {
          const searchable =
            prompt.name.toLowerCase() +
            ' ' +
            prompt.description.toLowerCase() +
            ' ' +
            prompt.content.toLowerCase();
          return searchable.includes(searchTerm.toLowerCase());
        }),
      });
    } else {
      promptDispatch({ field: 'filteredPrompts', value: prompts });
    }
  }, [searchTerm, prompts, promptDispatch]);

  if (!showPromptbar) {
    return (
      <button
        onClick={handleTogglePromptbar}
        aria-label="Open prompt library"
        className="fixed bottom-24 right-3 z-30 grid h-9 w-9 place-items-center rounded-full border border-border bg-white text-ink-500 shadow-ui-sm hover:bg-ink-50"
      >
        <Icon.Cards width={16} height={16} />
      </button>
    );
  }

  const unfiledPrompts = filteredPrompts.filter((p) => !p.folderId).slice().reverse();

  return (
    <PromptbarContext.Provider
      value={{
        ...promptBarContextValue,
        handleCreatePrompt,
        handleDeletePrompt,
        handleUpdatePrompt,
      }}
    >
      {/* Overlay backdrop below xl */}
      <div
        onClick={handleTogglePromptbar}
        className="fixed inset-0 z-40 animate-[drawerBackdropIn_0.2s_ease] bg-ink-900/40 backdrop-blur-sm xl:hidden"
      />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-[300px] max-w-[85vw] flex-shrink-0 animate-[drawerInRight_0.22s_ease] flex-col overflow-y-auto border-l border-border bg-surface-paper-2 shadow-xl xl:static xl:z-auto xl:max-w-none xl:animate-none xl:shadow-none">
        {/* Header */}
        <div className="flex items-center border-b border-border px-[18px] py-3.5">
          <span className="t-eyebrow text-[10.5px]">
            {t('Prompt library')}
          </span>
          <div className="ml-auto flex gap-1.5">
            <button
              onClick={() => handleCreateFolder(t('New folder'), 'prompt')}
              title={t('New folder')}
              className="text-ink-400 hover:text-ink-600"
            >
              <Icon.Folder width={13} height={13} />
            </button>
            <button
              onClick={handleCreatePrompt}
              title={t('New prompt')}
              className="text-ink-400 hover:text-ink-600"
            >
              <Icon.Plus width={14} height={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-[18px]">
          {/* Search */}
          <div className="relative mb-4">
            <Icon.Search
              width={14}
              height={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              value={searchTerm}
              onChange={(e) =>
                promptDispatch({ field: 'searchTerm', value: e.target.value })
              }
              placeholder={t('Search prompts...')}
              className="w-full rounded-md border border-border bg-white py-[7px] pl-[30px] pr-3 font-sans text-[12.5px] text-ink-800 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {/* Foldered prompts */}
          <PromptFolders />

          {/* Unfiled prompt list */}
          <div
            className="grid gap-1"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {unfiledPrompts.length > 0 ? (
              unfiledPrompts.map((prompt) => (
                <PromptItem
                  key={prompt.id}
                  prompt={prompt}
                  onEdit={(updated) => handleUpdatePrompt(updated)}
                  onDelete={() => handleDeletePrompt(prompt)}
                />
              ))
            ) : (
              <div className="py-6 text-center text-[12.5px] text-ink-400">
                {searchTerm ? t('No matching prompts') : t('No prompts yet')}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-3">
          <PromptbarSettings handleSend={handleSend} />
        </div>
      </aside>
    </PromptbarContext.Provider>
  );
};

function PromptItem({ prompt, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      {editing && (
        <PromptModal
          prompt={prompt}
          onClose={() => setEditing(false)}
          onUpdatePrompt={(updated) => {
            onEdit(updated);
            setEditing(false);
          }}
        />
      )}
    <div
      role="button"
      tabIndex={0}
      aria-label={`Edit prompt: ${prompt.name}`}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); } }}
      draggable="true"
      onDragStart={(e) => {
        if (e.dataTransfer) {
          e.dataTransfer.setData('prompt', JSON.stringify(prompt));
        }
      }}
      className="group cursor-pointer rounded-md border border-border bg-white px-2.5 py-2 hover:border-brand-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-ink-900">
            {prompt.name}
          </div>
          {prompt.description && (
            <div className="mt-0.5 truncate text-[10.5px] text-ink-500">
              {prompt.description}
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(t('Delete this prompt?'))) onDelete();
          }}
          aria-label="Delete prompt"
          className="invisible flex-shrink-0 p-0.5 text-ink-400 hover:text-status-red-500 group-hover:visible group-focus-within:visible focus-visible:visible"
        >
          <Icon.X width={12} height={12} />
        </button>
      </div>
    </div>
    </>
  );
}

export default Promptbar;
